/**
 * Thread @-mention autocomplete — pure core (group-threading P2b follow-up).
 *
 * Powers the composer dropdown: given the account roster and the in-progress
 * `@query` (from `detectActiveMention`), rank the matching members and, for each,
 * derive the plain `@handle` the composer will insert. The handle is chosen so the
 * on-send resolver (`resolveMentionsToUids`) maps it back to EXACTLY the picked uid
 * — verified against the live roster — so the summon targets the right person while
 * the message text stays human-readable (no raw `<@uid>` tokens, no non-editable
 * pills in the fragile contentEditable). Pure + deterministic + React-free.
 */
import { resolveMentionsToUids } from './mentionResolver';
import type { MentionRosterEntry } from './mentionResolver';

export interface MentionCandidate {
  uid: string;
  /** Human label for the row: displayName → email → uid. */
  label: string;
  /** Email shown as a muted second line only when a distinct displayName exists. */
  secondary?: string;
  /** The plain, resolver-safe handle inserted after `@` (e.g. `qa-teammate`). */
  handle: string;
}

const DEFAULT_LIMIT = 8;
/** Chars the on-send `PLAIN_MENTION` regex accepts inside a `@query`. */
const HANDLE_SAFE = /[^A-Za-z0-9._%+-]/g;

function norm(v: string | undefined): string {
  return (v ?? '').trim().toLowerCase();
}
function nameKey(v: string | undefined): string {
  return norm(v).replace(/\s+/g, '');
}
function emailLocal(email: string | undefined): string {
  const e = norm(email);
  const at = e.indexOf('@');
  return at > 0 ? e.slice(0, at) : e;
}
/** Strip anything the resolver's plain-mention regex would not capture. */
function sanitizeHandle(v: string): string {
  return (v ?? '').trim().replace(HANDLE_SAFE, '');
}

/**
 * Derive the plain handle to insert for `member`, verified against `roster` so
 * `resolveMentionsToUids('@' + handle, roster)` returns exactly `[member.userId]`.
 * Tries the most readable forms first (email local-part, whitespace-stripped
 * display name), then falls back to the uid — which the resolver always matches
 * exactly and uniquely. Never returns empty for a member with a uid.
 */
export function deriveMentionHandle(
  member: MentionRosterEntry,
  roster: ReadonlyArray<MentionRosterEntry>,
): string {
  const uid = String(member?.userId ?? '').trim();
  if (!uid) return '';

  // READABLE FORMS ONLY (2026-09-08): the uid used to sit in this list, and
  // because a uid always verifies uniquely it won this loop the moment neither
  // readable form did — which is how a raw id reached a client-visible thread.
  // The uid is now reachable only through the floor below, where a member has
  // no other identifier in existence.
  // The profile's e-mail stands in for a blank member-doc e-mail (2026-09-08).
  const options = [emailLocal(member.email || member.profileEmail), nameKey(member.displayName)]
    .map(sanitizeHandle)
    .filter((h) => h.length > 0);

  for (const handle of options) {
    const resolved = resolveMentionsToUids(`@${handle}`, roster);
    if (resolved.length === 1 && resolved[0] === uid) return handle;
  }

  // A RAW UID NEVER GOES INTO MESSAGE TEXT (2026-09-08 report): the old floor
  // returned the uid whenever no readable form verified, and a group thread in a
  // client's workspace showed `@kFZ5MgOgqkZn7tXy0XWtiW13hu72` mid-sentence. The
  // resolver's exact-identifier tier makes an email local-part unique per account,
  // so this floor is now essentially unreachable — but when it IS reached, a
  // readable handle that fails to summon beats an unreadable one that succeeds:
  // the sender can see and fix what they wrote, and the fail-closed rule means
  // nobody is summoned by mistake either way.
  const readable = sanitizeHandle(emailLocal(member.email || member.profileEmail)) || sanitizeHandle(nameKey(member.displayName));
  if (readable) return readable;
  // Nothing readable exists on this member at all (no email, no name): the uid
  // is the only identifier left, and the resolver matches it exactly.
  return sanitizeHandle(uid) || uid;
}

/**
 * The assistant's synthetic candidate (owner directive 2026-08-26: "when
 * someone mentions @p - top result should be @Prest0n"). Prest0n is not a
 * roster member, so without this the one participant every thread shares was
 * the one name the dropdown could not offer. The handle inserts the exact
 * spelling the summon gate's `mentionsAssistant` recognises, capital P per
 * the brand rule.
 */
export const ASSISTANT_MENTION_CANDIDATE: MentionCandidate = {
  uid: 'assistant',
  label: 'Prest0n',
  secondary: 'AI teammate',
  handle: 'Prest0n',
};

/** Query keys the assistant candidate answers to. */
const ASSISTANT_QUERY_KEYS = ['prest0n', 'preston'];

export function assistantMatchesMentionQuery(query: string): boolean {
  const q = norm(query);
  if (!q) return true; // a bare "@" offers everyone — Prest0n included
  return ASSISTANT_QUERY_KEYS.some((k) => k.startsWith(q) || k.includes(q));
}

/**
 * Rank the roster for the active `@query`. Excludes self and blank uids, de-dupes by
 * uid (first wins). Empty query → Prest0n, then the whole roster (label-sorted).
 * Otherwise, ranked by WHAT matched (owner directive 2026-08-26 — people are
 * found by their NAME; usernames/emails are the secondary form):
 *   0. Prest0n, whenever the query could mean it ("@p" → @Prest0n on top);
 *   1. display-name prefix matches;
 *   2. email / uid prefix matches;
 *   3. name substring matches;
 *   4. email / uid substring matches.
 * Ties sort by label. Returns at most `limit` candidates, each with a derived handle.
 */
export function filterMentionCandidates(
  roster: ReadonlyArray<MentionRosterEntry>,
  query: string,
  selfId: string | null | undefined,
  limit: number = DEFAULT_LIMIT,
): MentionCandidate[] {
  const self = String(selfId ?? '').trim();
  const q = norm(query);

  const seen = new Set<string>();
  const base: MentionRosterEntry[] = [];
  for (const m of roster ?? []) {
    const uid = String(m?.userId ?? '').trim();
    if (!uid || uid === self || seen.has(uid)) continue;
    seen.add(uid);
    base.push(m);
  }

  const labelOf = (m: MentionRosterEntry): string =>
    (m.displayName || '').trim() || (m.email || '').trim() || String(m.userId).trim();
  const byLabel = (a: MentionRosterEntry, b: MentionRosterEntry): number =>
    labelOf(a).toLocaleLowerCase().localeCompare(labelOf(b).toLocaleLowerCase()) ||
    String(a.userId).localeCompare(String(b.userId));

  let ranked: MentionRosterEntry[];
  if (!q) {
    ranked = [...base].sort(byLabel);
  } else {
    const scored = base
      .map((m) => {
        const nameKeys = [nameKey(m.displayName)].filter((k) => k.length > 0);
        const handleKeys = [norm(m.email), emailLocal(m.email), norm(m.userId)].filter(
          (k) => k.length > 0,
        );
        // Names beat usernames at every tier: a name PREFIX outranks a
        // username prefix, and a name SUBSTRING outranks a username
        // substring — the person someone is looking for is found by how
        // they're known, not by their login.
        const rank = nameKeys.some((k) => k.startsWith(q)) ? 0
          : handleKeys.some((k) => k.startsWith(q)) ? 1
          : nameKeys.some((k) => k.includes(q)) ? 2
          : handleKeys.some((k) => k.includes(q)) ? 3
          : 4;
        return { m, rank };
      })
      .filter((s) => s.rank < 4);
    scored.sort((a, b) => a.rank - b.rank || byLabel(a.m, b.m));
    ranked = scored.map((s) => s.m);
  }

  const humans = ranked.slice(0, Math.max(0, limit)).map((m) => {
    const label = labelOf(m);
    const email = (m.email || '').trim();
    const displayName = (m.displayName || '').trim();
    return {
      uid: String(m.userId).trim(),
      label,
      secondary: displayName && email ? email : undefined,
      handle: deriveMentionHandle(m, roster),
    };
  });

  // Prest0n rides on top whenever the query could mean it — never displacing
  // a human below the limit (the assistant is additive, not competitive).
  return assistantMatchesMentionQuery(q)
    ? [ASSISTANT_MENTION_CANDIDATE, ...humans]
    : humans;
}
